/**
 * HUD Top Bar — DOM section.
 * Layout: Brand | Filler | Priest card | Wave-info | Light bar | Settings button.
 */

import type { GameStateView } from "../../application/ports.js";
import { t } from "../../i18n/index.js";

export interface TopBarCallbacks {
  onSettings?: () => void;
}

interface TopBarRefs {
  root: HTMLDivElement;
  brandIcon: HTMLSpanElement;
  brandTitle: HTMLSpanElement;
  fieldLabel: HTMLSpanElement;
  priest: HTMLDivElement;
  priestIcon: HTMLSpanElement;
  priestLevel: HTMLSpanElement;
  priestGrace: HTMLSpanElement;
  waveInfo: HTMLDivElement;
  waveChip: HTMLSpanElement;
  nextChip: HTMLSpanElement;
  lightLabel: HTMLSpanElement;
  lightValue: HTMLSpanElement;
  lightFill: HTMLDivElement;
  lightBar: HTMLDivElement;
  settingsBtn: HTMLButtonElement;
  localeBtn: HTMLButtonElement;
  audioBtn: HTMLButtonElement;
}

export function createTopBar(parent: HTMLElement, cbs: TopBarCallbacks): {
  update(state: GameStateView): void;
} {
  // Build DOM
  const root = document.createElement("div");
  root.className = "hud-topbar";

  // Brand
  const brand = document.createElement("div");
  brand.className = "hud-topbar__brand";
  const brandIcon = document.createElement("span");
  brandIcon.className = "hud-topbar__brand-icon";
  brandIcon.textContent = "";
  const brandTitle = document.createElement("span");
  brandTitle.className = "hud-topbar__brand-title";
  brandTitle.textContent = "";
  const fieldLabel = document.createElement("span");
  fieldLabel.className = "hud-topbar__field-label";
  brand.appendChild(brandIcon);
  brand.appendChild(brandTitle);
  brand.appendChild(fieldLabel);

  // Filler
  const filler = document.createElement("div");
  filler.className = "hud-topbar__filler";

  // Priest card
  const priest = document.createElement("div");
  priest.className = "hud-topbar__priest";
  const priestIcon = document.createElement("span");
  priestIcon.className = "hud-topbar__priest-icon";
  priestIcon.textContent = "";
  const priestLevel = document.createElement("span");
  priestLevel.className = "hud-topbar__priest-level";
  const priestSep = document.createElement("span");
  priestSep.className = "hud-topbar__priest-sep";
  priestSep.textContent = "·";
  const priestGrace = document.createElement("span");
  priestGrace.className = "hud-topbar__priest-grace";
  priest.appendChild(priestIcon);
  priest.appendChild(priestLevel);
  priest.appendChild(priestSep);
  priest.appendChild(priestGrace);

  // Wave info (current + next, battle-only)
  const waveInfo = document.createElement("div");
  waveInfo.className = "hud-topbar__waveinfo";
  const waveChip = document.createElement("span");
  waveChip.className = "hud-topbar__wave-chip";
  const nextChip = document.createElement("span");
  nextChip.className = "hud-topbar__wave-chip hud-topbar__wave-chip--next";
  waveInfo.appendChild(waveChip);
  waveInfo.appendChild(nextChip);

  // Light bar
  const lightBar = document.createElement("div");
  lightBar.className = "hud-topbar__light";
  const lightLabel = document.createElement("span");
  lightLabel.className = "hud-topbar__light-label";
  lightLabel.textContent = t("hud.mana");
  const lightValue = document.createElement("span");
  lightValue.className = "hud-topbar__light-value";
  const lightTrack = document.createElement("div");
  lightTrack.className = "hud-topbar__light-track";
  const lightFill = document.createElement("div");
  lightFill.className = "hud-topbar__light-fill";
  lightTrack.appendChild(lightFill);
  lightBar.appendChild(lightLabel);
  lightBar.appendChild(lightValue);
  lightBar.appendChild(lightTrack);

  // Settings button
  const settingsBtn = document.createElement("button");
  settingsBtn.className = "hud-topbar__settings-btn";
  settingsBtn.textContent = "⚙ " + t("hud.settings");
  settingsBtn.addEventListener("click", () => {
    cbs.onSettings?.();
    console.log("[HUD] settings clicked");
  });

  // Locale toggle button (icon-only, moved from above-canvas strip)
  const localeBtn = document.createElement("button");
  localeBtn.id = "locale-toggle-btn";
  localeBtn.className = "hud-topbar__icon-btn";
  localeBtn.type = "button";
  localeBtn.title = "Toggle language";

  // Audio toggle button (icon-only, moved from above-canvas strip)
  const audioBtn = document.createElement("button");
  audioBtn.id = "audio-toggle-btn";
  audioBtn.className = "hud-topbar__icon-btn";
  audioBtn.type = "button";
  audioBtn.title = "Mute/unmute";
  audioBtn.textContent = "🔊";

  const rightTray = document.createElement("div");
  rightTray.className = "hud-topbar__right-tray";
  rightTray.appendChild(priest);
  rightTray.appendChild(waveInfo);
  rightTray.appendChild(lightBar);
  rightTray.appendChild(settingsBtn);
  rightTray.appendChild(localeBtn);
  rightTray.appendChild(audioBtn);

  root.appendChild(brand);
  root.appendChild(filler);
  root.appendChild(rightTray);
  parent.appendChild(root);

  const refs: TopBarRefs = { root, brandIcon, brandTitle, fieldLabel, priest, priestIcon, priestLevel, priestGrace, waveInfo, waveChip, nextChip, lightLabel, lightValue, lightFill, lightBar, settingsBtn, localeBtn, audioBtn };

  let _lastMana = "";
  let _lastLevel = -1;
  let _lastGrace = -1;
  let _lastField = "";
  let _lastWaveKey = "";
  let _lastNextKey = "";
  let _lastBrandTitle = "";
  let _lastBrandIcon = "";
  let _lastMageIcon = "";
  let _lastMageLabelKey = "";
  let _lastResourceLabelKey = "";

  return {
    update(state: GameStateView): void {
      const isBattle = state.scene === "game";
      const campaignUi = state.campaignUi;
      const nextBrandTitle = campaignUi?.brandTitle ?? "";
      const nextBrandIcon = campaignUi?.brandIcon ?? "";
      const nextMageIcon = campaignUi?.mageIcon ?? "";
      const mageLabelKey = campaignUi?.mageLabelKey ?? "";
      const resourceLabelKey = campaignUi?.resourceLabelKey ?? "";

      if (nextBrandTitle !== _lastBrandTitle) {
        refs.brandTitle.textContent = nextBrandTitle;
        _lastBrandTitle = nextBrandTitle;
      }
      if (nextBrandIcon !== _lastBrandIcon) {
        refs.brandIcon.textContent = nextBrandIcon;
        _lastBrandIcon = nextBrandIcon;
      }
      if (nextMageIcon !== _lastMageIcon) {
        refs.priestIcon.textContent = nextMageIcon;
        _lastMageIcon = nextMageIcon;
      }

      // Light bar, settings, and wave-info only relevant during battle
      refs.lightBar.style.display = isBattle ? "" : "none";
      refs.settingsBtn.style.display = isBattle ? "" : "none";
      refs.waveInfo.style.display = isBattle ? "" : "none";
      // Priest card (level + grace) is map-only info — hide in battle to free up space.
      refs.priest.style.display = isBattle ? "none" : "";

      // Field label only in battle
      const fieldId = isBattle ? (state.field?.id ?? "") : "";
      if (fieldId !== _lastField) {
        refs.fieldLabel.textContent = fieldId ? " — " + fieldId : "";
        _lastField = fieldId;
      }

      // Priest card
      const level = state.map?.mage?.level ?? 0;
      const grace = state.map?.pointsLeft ?? 0;
      if (level !== _lastLevel || mageLabelKey !== _lastMageLabelKey) {
        refs.priestLevel.textContent = mageLabelKey ? t(mageLabelKey, { level }) : String(level);
        _lastLevel = level;
        _lastMageLabelKey = mageLabelKey;
      }
      if (grace !== _lastGrace || resourceLabelKey !== _lastResourceLabelKey) {
        refs.priestGrace.textContent = resourceLabelKey ? t(resourceLabelKey, { grace }) : String(grace);
        _lastGrace = grace;
        _lastResourceLabelKey = resourceLabelKey;
      }

      // Wave info chips
      if (isBattle) {
        const cw = state.currentWave;
        const cwKey = cw ? `${cw.index}:${cw.spawned}:${cw.totalMonsters}` : "none";
        if (cwKey !== _lastWaveKey) {
          _lastWaveKey = cwKey;
          if (cw) {
            refs.waveChip.style.display = "";
            refs.waveChip.textContent = t("hud.topbarWave", {
              index: String(cw.index + 1),
              spawned: String(cw.spawned),
              total: String(cw.totalMonsters),
            });
          } else {
            refs.waveChip.style.display = "none";
          }
        }
        const nw = state.nextWave;
        const nwKey = nw ? `${nw.type}:${nw.isBoss ? 1 : 0}` : "none";
        if (nwKey !== _lastNextKey) {
          _lastNextKey = nwKey;
          if (nw) {
            refs.nextChip.style.display = "";
            const mobType = t("hud.mobType." + nw.type);
            refs.nextChip.textContent = t("hud.topbarNext", {
              mobType: (nw.isBoss ? "★ " : "") + mobType,
            });
          } else {
            refs.nextChip.style.display = "none";
          }
        }
      }

      // Light bar
      if (state.mana !== undefined) {
        const { current, max } = state.mana;
        const manaStr = `${Math.floor(current)} / ${max}`;
        if (manaStr !== _lastMana) {
          refs.lightValue.textContent = manaStr;
          _lastMana = manaStr;
          const ratio = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
          refs.lightFill.style.width = `${ratio * 100}%`;
          if (ratio <= 0.3) {
            refs.lightFill.classList.add("hud-topbar__light-fill--danger");
          } else {
            refs.lightFill.classList.remove("hud-topbar__light-fill--danger");
          }
        }
      }
    },
  };
}
